import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';

const UKEK_BYTES_LENGTH = 32;
const HEX_BASE = 16;
const HEX_PAD_LENGTH = 2;
const PREVIEW_LENGTH = 32;

/**
 * Test function to verify crypto operations work end-to-end
 * This creates a test file, encrypts it, then decrypts it using hardcoded values
 */
export const testCryptoEndToEnd = createAppAsyncThunk(
  'helia/testCrypto',
  async(
    _args: Record<string, never>,
    { extra: { pocServices }, rejectWithValue }
  ) => {
    try {
      // Step 1: Create test data
      const testContent = "This is a test file for crypto verification!";
      const testFile = new File([testContent], 'test.txt', { type: 'text/plain' });

      // Step 2: Generate test UKEK (32 bytes)
      const testUkekBytes = crypto.getRandomValues(new Uint8Array(UKEK_BYTES_LENGTH));

      // Step 3: Generate test ML-KEM keypair
      const keyPair = await pocServices.cryptoService.generateMlKemKeyPair();

      // Step 4: Create test recipient
      const testRecipient = {
        id: 'test-user-123',
        publicKey: keyPair.publicKey
      };

      // Step 5: Encrypt the file
      const encryptionResult = await pocServices.fileEncryptionService.encryptFile(
        testFile,
        testUkekBytes,
        [testRecipient]
      );

      // Step 6: Decrypt the file using the private key
      const recipientResult = encryptionResult.recipientResults[0];

      const decryptionResult = await pocServices.fileEncryptionService.decryptFileAsRecipient(
        encryptionResult.encryptedFile,
        recipientResult,
        keyPair.privateKey
      );

      // Step 7: Verify content
      const decryptedContent = await decryptionResult.decryptedFile.text();

      if (decryptedContent === testContent) {
        return {
          success: true,
          testsPassed: [
            'File encryption',
            'File decryption',
            'Content verification'
          ],
          originalContent: testContent,
          decryptedContent
        };
      } else {
        throw new Error('Content verification failed');
      }

    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown crypto test error');
    }
  }
);

/**
 * Test UKEK generation to identify the key mismatch issue
 */
export const testUkekMismatch = createAppAsyncThunk(
  'helia/testUkekMismatch',
  async(
    _args: Record<string, never>,
    { extra: { pocServices }, getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const password = state.auth.password;
      const sessionToken = state.auth.sessionToken;

      if (password.length === 0 || sessionToken == null || sessionToken.length === 0) {
        throw new Error('Password and session token required for UKEK test');
      }

      // Method 1: deriveKey (used during login)
      const ukekFromDeriveKey = await pocServices.cryptoService.deriveKey(password, sessionToken);

      // Method 2: hashPassword (used during key generation) - use actual user email
      const userEmail = state.user.user?.email;
      let ukekFromHashPassword = '';

      if (userEmail != null && userEmail.length > 0) {
        const hashResult = await pocServices.cryptoService.hashPassword(password, userEmail);
        ukekFromHashPassword = hashResult.keyHex;
      }

      const currentUkek = state.auth.ukek;

      return {
        success: true,
        storedUkek: currentUkek.substring(0, PREVIEW_LENGTH),
        derivedUkek: ukekFromDeriveKey.substring(0, PREVIEW_LENGTH),
        hashPasswordUkek: ukekFromHashPassword.substring(0, PREVIEW_LENGTH),
        matches: {
          derivedVsStored: ukekFromDeriveKey === currentUkek,
          hashPasswordVsStored: ukekFromHashPassword === currentUkek,
          derivedVsHashPassword: ukekFromDeriveKey === ukekFromHashPassword
        }
      };

    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown UKEK test error');
    }
  }
);

/**
 * Test private key encryption/decryption cycle using existing authentication data
 */
export const testPrivateKeyEncryption = createAppAsyncThunk(
  'helia/testPrivateKeyEncryption',
  async(
    _args: Record<string, never>,
    { extra: { pocServices }, getState, rejectWithValue }
  ) => {
    try {
      // Step 1: Get current user authentication data
      const state = getState();
      const ukek = state.auth.ukek;

      if (ukek.length === 0) {
        throw new Error('UKEK not available - user must be logged in for this test');
      }

      // Step 2: Generate test private key
      const keyPair = await pocServices.cryptoService.generateMlKemKeyPair();
      const testPrivateKey = keyPair.privateKey;

      // Step 3: Create UKEK hash structure for encryption
      const ukekHashResult = await pocServices.cryptoService.hashPassword('test-password', 'test@example.com');
      // Override with our actual UKEK
      ukekHashResult.keyHex = ukek;

      const encryptedPrivateKeyHex = await pocServices.cryptoService.encryptPrivateKey(
        testPrivateKey,
        ukekHashResult
      );

      // Step 4: Decrypt the private key using raw UKEK bytes
      const ukekBytes = new Uint8Array(ukek.match(/.{1,2}/g)?.map(byte => Number.parseInt(byte, HEX_BASE)) ?? []);
      const decryptedPrivateKey = await pocServices.cryptoService.decryptPrivateKey(
        encryptedPrivateKeyHex,
        ukekBytes
      );

      // Step 5: Verify they match
      const originalHex = Array.from(testPrivateKey)
        .map(b => b.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, '0'))
        .join('');
      const decryptedHex = Array.from(decryptedPrivateKey)
        .map(b => b.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, '0'))
        .join('');

      if (originalHex === decryptedHex) {
        return {
          success: true,
          testsPassed: [
            'Private key encryption',
            'Private key decryption',
            'Key integrity verification'
          ]
        };
      } else {
        throw new Error('Private key verification failed');
      }

    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown private key test error');
    }
  }
);
